<template>
  <aside class="resources-sidebar">
    <!-- 资源类别 -->
    <section v-if="activeResourceTypes.length" class="filter-group">
      <h3 class="filter-group__title">资源类别</h3>
      <ul class="filter-group__list">
        <li
          v-for="item in visibleResourceTypes"
          :key="item.key"
          class="filter-group__item"
        >
          <button
            type="button"
            class="filter-group__btn"
            :class="{ 'filter-group__btn--active': activeResourceType === item.key }"
            @click="selectResourceType(item.key)"
          >
            <span class="filter-group__label">{{ item.label }}</span>
            <span class="filter-group__count">({{ formatCount(item.count) }})</span>
          </button>
        </li>
      </ul>
      <button
        v-if="activeResourceTypes.length > typeShowLimit"
        type="button"
        class="filter-group__more"
        @click="typeShowAll = !typeShowAll"
      >
        {{ typeShowAll ? '收起' : '更多' }}
      </button>
    </section>

    <!-- 作者 -->
    <section v-if="activeAuthors.length" class="filter-group">
      <h3 class="filter-group__title">作者</h3>
      <ul class="filter-group__list">
        <li
          v-for="item in visibleAuthors"
          :key="item.key"
          class="filter-group__item"
        >
          <button
            type="button"
            class="filter-group__btn"
            :class="{ 'filter-group__btn--active': activeAuthor === item.key }"
            @click="selectFilter('author', item.key)"
          >
            <span class="filter-group__label">{{ item.label }}</span>
            <span class="filter-group__count">({{ formatCount(item.count) }})</span>
          </button>
        </li>
      </ul>
      <button
        v-if="activeAuthors.length > authorShowLimit"
        type="button"
        class="filter-group__more"
        @click="authorShowAll = !authorShowAll"
      >
        {{ authorShowAll ? '收起' : '更多' }}
      </button>
    </section>

    <!-- 语种 -->
    <section v-if="activeLanguages.length" class="filter-group">
      <h3 class="filter-group__title">语种</h3>
      <ul class="filter-group__list">
        <li
          v-for="item in visibleLanguages"
          :key="item.key"
          class="filter-group__item"
        >
          <button
            type="button"
            class="filter-group__btn"
            :class="{ 'filter-group__btn--active': activeLanguage === item.key }"
            @click="selectFilter('language', item.key)"
          >
            <span class="filter-group__label">{{ item.label }}</span>
            <span class="filter-group__count">({{ formatCount(item.count) }})</span>
          </button>
        </li>
      </ul>
      <button
        v-if="activeLanguages.length > languageShowLimit"
        type="button"
        class="filter-group__more"
        @click="languageShowAll = !languageShowAll"
      >
        {{ languageShowAll ? '收起' : '更多' }}
      </button>
    </section>
  </aside>
</template>

<script>
export default {
  name: 'ResourcesSidebar',
  props: {
    resourceTypes: { type: Array, default: () => [] },
    authors: { type: Array, default: () => [] },
    languages: { type: Array, default: () => [] },
    activeResourceType: { type: String, default: '' },
    activeAuthor: { type: String, default: '' },
    activeLanguage: { type: String, default: '' }
  },
  data() {
    return {
      typeShowAll: false,
      authorShowAll: false,
      languageShowAll: false,
      typeShowLimit: 4,
      authorShowLimit: 4,
      languageShowLimit: 4
    }
  },
  computed: {
    activeResourceTypes() {
      return this.withPositiveCount(this.resourceTypes)
    },
    activeAuthors() {
      return this.withPositiveCount(this.authors)
    },
    activeLanguages() {
      return this.withPositiveCount(this.languages)
    },
    visibleResourceTypes() {
      return this.typeShowAll
        ? this.activeResourceTypes
        : this.activeResourceTypes.slice(0, this.typeShowLimit)
    },
    visibleAuthors() {
      return this.authorShowAll
        ? this.activeAuthors
        : this.activeAuthors.slice(0, this.authorShowLimit)
    },
    visibleLanguages() {
      return this.languageShowAll
        ? this.activeLanguages
        : this.activeLanguages.slice(0, this.languageShowLimit)
    }
  },
  methods: {
    withPositiveCount(list) {
      return (list || []).filter((item) => Number(item && item.count) > 0)
    },
    formatCount(num) {
      return Number(num || 0).toLocaleString()
    },
    selectResourceType(key) {
      this.$emit('update:activeResourceType', key)
    },
    selectFilter(field, key) {
      const map = {
        author: 'update:activeAuthor',
        language: 'update:activeLanguage'
      }
      if (map[field]) {
        this.$emit(map[field], key)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.resources-sidebar {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-base;
  align-self: flex-start;
  position: sticky;
  top: calc($header-height + $spacing-base);
}

.filter-group {
  background: rgba($color-cream, 0.95);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba($color-brown-dark, 0.06);

  &__title {
    padding: $spacing-md $spacing-base;
    font-size: rem(14);
    font-weight: 600;
    color: $color-white;
    background: $color-brown-dark;
  }

  &__list {
    padding: $spacing-xs 0;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: $spacing-sm $spacing-base;
    font-size: rem(13);
    color: $color-text;
    text-align: left;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: rgba($color-brown-dark, 0.06);
    }

    &--active {
      background: rgba($success-color, 0.12);
      color: $success-color;
      font-weight: 500;
    }
  }

  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    font-size: rem(12);
    color: $color-text-light;
    flex-shrink: 0;
  }

  &__btn--active &__count {
    color: inherit;
    opacity: 0.85;
  }

  &__more {
    display: block;
    width: 100%;
    padding: $spacing-sm;
    font-size: rem(12);
    color: $success-color;
    text-align: center;
    border-top: 1px solid rgba($color-border, 0.4);
    transition: background 0.2s;

    &:hover {
      background: rgba($success-color, 0.06);
    }
  }
}

@media (max-width: 960px) {
  .resources-sidebar {
    width: 100%;
    position: static;
  }
}
</style>
